import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
  QuoteResponse,
  QuoteStatusEnum,
  UpdateQuoteParams,
  QuoteLine,
  QuoteItemLine,
  QuoteDiscountLine,
  QuoteSectionLine,
  QuoteNoteLine,
  Item,
  HydratedQuoteLine,
  generateQuoteLineId,
  UpdateItemParams,
  CustomFieldsSchema,
  CurrencyCode,
  CURRENCY_CODES,
  CustomFieldValues,
  ExchangeRateSnapshot,
  ExchangeRateSnapshotSchema,
  convertQuoteAmountToBase,
  convertQuoteLineAmount,
  convertUnitPrice,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Plus,
  Save,
  Pencil,
  FileText,
  Archive,
  ArchiveRestore,
  Loader,
} from "lucide-react";
import { useRecordQuotesStore } from "./quotesStore";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  QuoteMetaBar,
  QuoteItemsTable,
  QuoteDiscountsTable,
  QuoteTotalsFooter,
  ExpandCustomFieldsToggle,
} from "./components";
import { CharacterLimitNote } from "@/components/CharacterLimitNote";
import { RichTextEditorSheet } from "@/components/RichTextEditorSheet";
import { MarkdownReact } from "@/components/MarkdownReact";
import { z } from "zod";
import { fetchApi } from "@/lib/api";
import {
  quoteCurrencyRate,
  type QuoteCurrencyFields,
} from "@/lib/quoteCurrency";
import { QuoteCurrencySelector } from "./components/QuoteCurrencySelector";
import { CoverLetterSection } from "./components/CoverLetterSection";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";
import ItemEditor from "@/modules/items/ItemEditor";
import { useItemsStore } from "@/modules/items/itemsStore";
import { NotesSection } from "@/components/notes/NotesSection";
import { useOrganizationStore } from "@/store/organizationStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { usePeopleStore } from "@/modules/people/peopleStore";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { QuoteDocumentTab } from "@/modules/quoteDocuments/QuoteDocumentTab";

// Editable fields that the parent controls
export interface QuoteEditorData {
  name: string;
  customerCompanyId: string | null;
  priceListId: string | null;
  shippingAddressId: string | null;
  locationId: string | null;
  opportunityId?: string | null;
  salesRepUserId: string | null;
  personIds: string[];
  deliveryTerms: string;
  paymentTerms: string;
  termsOfService: string;
  quoteFooter: string;
  expirationDate: string; // yyyy-MM-dd format
  quoteDate: string; // yyyy-MM-dd format
  lines: QuoteLine[];
  coverLetterMarkdown: string;
  showTotals: boolean;
  customFields: CustomFieldValues;
  quoteCurrency: CurrencyCode | null;
  snapshot: ExchangeRateSnapshot | null;
}

interface QuoteEditorProps {
  // The full quote response (for display of calculated fields, status, etc.)
  quote?: QuoteResponse | null;
  // The editable data (controlled by parent)
  data: QuoteEditorData;
  // Hydrated lines from the server (for calculated prices)
  hydratedLines?: HydratedQuoteLine[];
  // Whether fields have unsaved changes
  isDirty: boolean;
  readOnly?: boolean;
  // Whether the user may revoke/restore the public link. Separate from
  // readOnly: revoking targets finalized quotes, which are not editable.
  canManagePublicAccess?: boolean;
  // Whether the opportunity link may be edited. Separate from readOnly:
  // the link stays editable on finalized quotes.
  canEditOpportunity?: boolean;
  showHeader?: boolean;
  showActionButtons?: boolean;
  isSaving?: boolean;
  showTotalWeight?: boolean;
  enableCoverLetter?: boolean;
  showLocationField?: boolean;
  customFieldsSchema?: CustomFieldsSchema | null;
  /**
   * When true, the item editor renders inline over the quote instead of as a
   * full-screen sheet. Also requires the `estimation-station` flag for the org.
   */
  inlineItemEditor?: boolean;
  /**
   * Called when the inline item editor opens or closes. Lets an ancestor (e.g.
   * the review panel) disable quote-level approval while an item is being edited,
   * since inline item edits are manual-save and would otherwise be bypassed.
   */
  onInlineItemEditorOpenChange?: (open: boolean) => void;
  /** Inline item editor only: toggles the surrounding chat panel from the item editor header. */
  onToggleChat?: () => void;
  /** Inline item editor only: whether the surrounding chat panel is collapsed. */
  isChatCollapsed?: boolean;
  /** True while the agent turn is running, so the quote may still change. */
  isAgentWorking?: boolean;
  // Called when any field changes - parent updates state and queues save
  onDataChange: (data: QuoteEditorData) => void;
  onSave?: () => void;
  onCancel?: () => void;
  onFinalize?: () => void;
  finalizeReviewThreadId?: string;
  onCreateRevision?: () => void;
  onRefresh?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  isArchiving?: boolean;
  // Called after the document tab autosaves, with the saved document HTML
  onDocumentSaved?: (html: string) => void;
}

/**
 * Quote editor with header, lines, discounts, and totals, offering only the discount options the org's quote settings allow.
 * @param props - The quote, editor data, and change handlers
 * @returns The quote editor
 */
export default function QuoteEditor({
  quote,
  data,
  hydratedLines,
  isDirty,
  readOnly = false,
  canManagePublicAccess = false,
  canEditOpportunity = false,
  showHeader = true,
  showActionButtons = true,
  isSaving = false,
  showTotalWeight = false,
  enableCoverLetter = false,
  showLocationField = false,
  customFieldsSchema,
  inlineItemEditor = false,
  onInlineItemEditorOpenChange,
  onToggleChat,
  isChatCollapsed,
  isAgentWorking,
  onDataChange,
  onSave,
  onCancel,
  onFinalize,
  finalizeReviewThreadId,
  onCreateRevision,
  onRefresh,
  onArchive,
  onUnarchive,
  isArchiving,
  onDocumentSaved,
}: QuoteEditorProps) {
  // Whether a suggested exchange rate is currently being fetched
  const [isRateLoading, setIsRateLoading] = useState(false);

  // Always holds the latest data prop so async handlers can read editor state
  // that advanced while a fetch was in flight, instead of a stale closure copy.
  const dataRef = useRef(data);
  dataRef.current = data;

  const [editorView, setEditorView] = useState<"quote" | "document">("quote");
  const quoteDocumentsEnabled = useFeatureFlag("quote-documents");
  const quoteSettings = useQuoteSettingsStore((state) => state.settings);
  const fetchQuoteSettings = useQuoteSettingsStore(
    (state) => state.fetchSettings,
  );
  useEffect(() => {
    if (!quoteSettings) {
      fetchQuoteSettings();
    }
  }, [quoteSettings, fetchQuoteSettings]);
  const itemSettingsFetched = useItemSettingsStore((state) => state.hasFetched);
  const fetchItemSettings = useItemSettingsStore(
    (state) => state.fetchSettings,
  );
  useEffect(() => {
    if (!itemSettingsFetched) {
      fetchItemSettings();
    }
  }, [itemSettingsFetched, fetchItemSettings]);
  const allowPerQuoteDocumentEditing =
    quoteSettings?.allowPerQuoteDocumentEditing ?? false;
  const publicQuote = useRecordQuotesStore((state) =>
    quote?.id ? state.publicQuotes.get(quote.id) : undefined,
  );
  const documentTabAvailable =
    quoteDocumentsEnabled && !!quote?.quoteDocument && !!publicQuote;
  const canShowDocumentTab = documentTabAvailable && editorView === "document";

  // The org's active exchange rate snapshot. The currency selector is hidden
  // entirely until rates are configured, and only offers currencies reachable
  // from the base currency in this snapshot.
  const [activeSnapshot, setActiveSnapshot] =
    useState<ExchangeRateSnapshot | null>(null);

  // BOM Panel state (local UI state, not quote data)
  const [bomPanelOpen, setBomPanelOpen] = useState(false);
  const [bomItemId, setBomItemId] = useState<string | null>(null);
  const [bomQuantities, setBomQuantities] = useState<string[]>([]);

  // The inline item editor is part of Estimation Station; orgs without the flag
  // keep the full-screen sheet even in the thread panel.
  const estimationStationEnabled = useFeatureFlag("estimation-station");
  const inlineEditorEnabled = inlineItemEditor && estimationStationEnabled;

  // Notify ancestors while the inline item editor is open so quote-level
  // approval can be blocked (inline item edits are manual-save).
  const inlineEditorOpen = Boolean(
    inlineEditorEnabled && bomPanelOpen && bomItemId,
  );
  useEffect(() => {
    onInlineItemEditorOpenChange?.(inlineEditorOpen);
    return () => onInlineItemEditorOpenChange?.(false);
  }, [inlineEditorOpen, onInlineItemEditorOpenChange]);

  // Terms sheet states
  const [tosPanelOpen, setTosPanelOpen] = useState(false);
  const [footerPanelOpen, setFooterPanelOpen] = useState(false);
  const [deliveryPanelOpen, setDeliveryPanelOpen] = useState(false);
  const [paymentPanelOpen, setPaymentPanelOpen] = useState(false);

  // Computed line arrays
  const nonDiscountLines = useMemo(
    () => data.lines.filter((line) => line.type !== "DISCOUNT"),
    [data.lines],
  );

  const discountLines = useMemo(
    () =>
      data.lines.filter(
        (line) => line.type === "DISCOUNT",
      ) as QuoteDiscountLine[],
    [data.lines],
  );

  const hasMultiQuantityItems = useMemo(
    () =>
      data.lines.some(
        (line) => line.type === "ITEM" && line.quantityBreaks.length > 1,
      ),
    [data.lines],
  );

  const discountsDisabled = hasMultiQuantityItems || !data.showTotals;
  const allowDiscountLines = quoteSettings?.allowDiscountLines ?? true;

  const baseCurrency: CurrencyCode = quote?.baseCurrency ?? "USD";

  // Show the per-quote currency selector only when multi-currency is enabled, or
  // when this quote already uses a non-base currency (so it stays editable even
  // if an admin later disables the feature).
  const multiCurrencyEnabled = useOrganizationStore(
    (state) => state.organization?.multiCurrencyEnabled === true,
  );
  const showCurrencySelector =
    multiCurrencyEnabled ||
    (!!data.quoteCurrency && data.quoteCurrency !== baseCurrency);

  const currencyContext: QuoteCurrencyFields = {
    baseCurrency,
    quoteCurrency: data.quoteCurrency,
    snapshot: data.snapshot,
    quotePriceRounding: quote?.quotePriceRounding ?? null,
  };

  // Display-only base→quote rate derived from the pinned snapshot.
  const displayRate = quoteCurrencyRate(currencyContext)?.rate ?? "";

  // The hydrated lines were computed under the persisted quote's currency and
  // pinned snapshot. When the editor's currency context has moved off that
  // (a currency switch or a re-pinned rate), the backend-stamped display
  // amounts are stale and must not be rendered under the new context.
  const currencyContextDirty =
    (data.quoteCurrency ?? null) !== (quote?.quoteCurrency ?? null) ||
    (data.snapshot?.id ?? null) !== (quote?.snapshot?.id ?? null);

  const discountsDisabledReason = hasMultiQuantityItems
    ? "Discounts are not supported when line items have multiple quantity options."
    : !data.showTotals
      ? "Discounts are not supported when totals are hidden on the public quote."
      : null;

  // Field change handlers - all just call onDataChange with updated data
  const handleCustomFieldChange = useCallback(
    (fieldName: string, value: CustomFieldValues[string]) => {
      onDataChange({
        ...data,
        customFields: { ...data.customFields, [fieldName]: value },
      });
    },
    [data, onDataChange],
  );

  const handleCoverLetterChange = useCallback(
    (coverLetterMarkdown: string) => {
      onDataChange({ ...data, coverLetterMarkdown });
    },
    [data, onDataChange],
  );

  const handleNameChange = useCallback(
    (name: string) => {
      onDataChange({ ...data, name });
    },
    [data, onDataChange],
  );

  const handleCustomerChange = useCallback(
    (customerCompanyId: string | null) => {
      onDataChange({ ...data, customerCompanyId, shippingAddressId: null });
    },
    [data, onDataChange],
  );

  const handleShippingAddressChange = useCallback(
    (shippingAddressId: string | null) => {
      onDataChange({ ...data, shippingAddressId });
    },
    [data, onDataChange],
  );

  const handleLocationChange = useCallback(
    (locationId: string | null) => {
      onDataChange({ ...data, locationId });
    },
    [data, onDataChange],
  );

  const handlePriceListChange = useCallback(
    (priceListId: string | null) => {
      onDataChange({ ...data, priceListId });
    },
    [data, onDataChange],
  );

  // Persists a lifecycle-only quote update, toasting the result and refreshing.
  const saveLifecycleUpdate = useCallback(
    async (
      params: UpdateQuoteParams,
      successMessage: string,
      errorMessage: string,
    ) => {
      if (!quote?.id) {
        return;
      }
      const { quote: updated } = await useRecordQuotesStore
        .getState()
        .updateQuote(quote.id, params);
      if (updated) {
        toast.success(successMessage);
        onRefresh?.();
      } else {
        toast.error(errorMessage);
      }
    },
    [quote?.id, onRefresh],
  );

  const handleOpportunityChange = useCallback(
    async (opportunityId: string | null) => {
      if (quote?.status !== QuoteStatusEnum.FINALIZED) {
        if (readOnly) {
          return;
        }
        onDataChange({
          ...data,
          opportunityId,
          personIds: prefillPersonIds(data.personIds, opportunityId),
        });
        return;
      }
      // The opportunity link is a lifecycle field, saved immediately on finalized quotes.
      await saveLifecycleUpdate(
        { opportunityId },
        opportunityId
          ? "Quote linked to opportunity"
          : "Quote removed from opportunity",
        "Failed to update opportunity link",
      );
    },
    [quote?.status, readOnly, data, onDataChange, saveLifecycleUpdate],
  );

  const handleSalesRepChange = useCallback(
    (salesRepUserId: string | null) => {
      onDataChange({ ...data, salesRepUserId });
    },
    [data, onDataChange],
  );

  const handlePeopleChange = useCallback(
    (personIds: string[]) => {
      onDataChange({ ...data, personIds });
    },
    [data, onDataChange],
  );

  const handleExpirationChange = useCallback(
    (expirationDate: string) => {
      onDataChange({ ...data, expirationDate });
    },
    [data, onDataChange],
  );

  const handleQuoteDateChange = useCallback(
    (quoteDate: string) => {
      onDataChange({ ...data, quoteDate });
    },
    [data, onDataChange],
  );

  const handleDeliveryTermsChange = useCallback(
    (deliveryTerms: string) => {
      onDataChange({ ...data, deliveryTerms });
    },
    [data, onDataChange],
  );

  const handlePaymentTermsChange = useCallback(
    (paymentTerms: string) => {
      onDataChange({ ...data, paymentTerms });
    },
    [data, onDataChange],
  );

  const handleTermsOfServiceChange = useCallback(
    (termsOfService: string) => {
      onDataChange({ ...data, termsOfService });
    },
    [data, onDataChange],
  );

  const handleQuoteFooterChange = useCallback(
    (quoteFooter: string) => {
      onDataChange({ ...data, quoteFooter });
    },
    [data, onDataChange],
  );

  const handleShowTotalsChange = useCallback(
    (showTotals: boolean) => {
      onDataChange({ ...data, showTotals });
    },
    [data, onDataChange],
  );

  // Fetches the currently active rate snapshot, used for display before save.
  // The backend pins this same active snapshot when the quote currency is saved,
  // so the rate shown matches what gets persisted.
  const fetchActiveSnapshot =
    useCallback(async (): Promise<ExchangeRateSnapshot | null> => {
      const response = await fetchApi("exchange-rates/active", "GET");
      const parsed = z
        .object({ snapshot: ExchangeRateSnapshotSchema.nullable() })
        .safeParse(response.data);
      if (!response.ok || !parsed.success) {
        return null;
      }
      return parsed.data.snapshot;
    }, []);

  // Fetch the active snapshot on mount so the currency selector can be hidden
  // when none is configured and limited to currencies it has rates for.
  useEffect(() => {
    let active = true;
    fetchActiveSnapshot().then((snapshot) => {
      if (active) {
        setActiveSnapshot(snapshot);
      }
    });
    return () => {
      active = false;
    };
  }, [fetchActiveSnapshot]);

  // Currencies offered in the selector: the base plus any currency reachable
  // from it in the active snapshot. Empty until a snapshot loads.
  const availableCurrencies = useMemo<CurrencyCode[]>(() => {
    if (!activeSnapshot) {
      return [];
    }
    const reachable = Object.keys(activeSnapshot.rates[baseCurrency] ?? {});
    return CURRENCY_CODES.filter(
      (code) => code === baseCurrency || reachable.includes(code),
    );
  }, [activeSnapshot, baseCurrency]);

  const handleCurrencyChange = useCallback(
    async (quoteCurrency: CurrencyCode) => {
      // The currency this switch is moving away from, used after the async
      // fetch to detect whether a later selection has superseded it.
      const fromCurrency = dataRef.current.quoteCurrency;

      if (quoteCurrency === baseCurrency) {
        // Stored document-currency values (manual overrides, AMOUNT discounts)
        // change denomination with the quote currency, so re-denominate them to
        // base at the outgoing snapshot to preserve their economic value.
        const current = dataRef.current;
        onDataChange({
          ...current,
          lines: redenominateLinesToBase(current.lines, {
            baseCurrency,
            quoteCurrency: current.quoteCurrency,
            snapshot: current.snapshot,
          }),
          quoteCurrency,
          snapshot: null,
        });
        return;
      }

      // Switch to a non-base currency atomically: fetch the snapshot before
      // emitting anything so autosave never persists an intermediate state
      // where base-valued lines sit under the new (non-base) currency. If the
      // fetch fails or returns null, leave the data untouched on the old
      // currency so nothing inconsistent can be saved.
      setIsRateLoading(true);
      try {
        const snapshot = await fetchActiveSnapshot();
        if (!snapshot) {
          return;
        }
        // Read the latest editor state after the await so line/name/currency
        // edits made while the fetch was in flight are preserved rather than
        // reverted to the pre-fetch closure copy.
        const latest = dataRef.current;
        // If the currency changed again mid-flight, this response is for a
        // superseded switch; bail so a later selection is not clobbered.
        if (latest.quoteCurrency !== fromCurrency) {
          return;
        }
        // Re-denominate the latest lines through base at the outgoing context,
        // then into the incoming quote currency at the fetched snapshot.
        const baseLines = redenominateLinesToBase(latest.lines, {
          baseCurrency,
          quoteCurrency: latest.quoteCurrency,
          snapshot: latest.snapshot,
        });
        onDataChange({
          ...latest,
          lines: redenominateLinesToQuote(baseLines, {
            baseCurrency,
            quoteCurrency,
            snapshot,
          }),
          quoteCurrency,
          snapshot,
        });
      } finally {
        setIsRateLoading(false);
      }
    },
    [onDataChange, baseCurrency, fetchActiveSnapshot],
  );

  // Line management handlers
  const handleAddItemLine = useCallback(() => {
    if (readOnly) {
      return;
    }
    const newLine: QuoteItemLine = {
      id: generateQuoteLineId(),
      type: "ITEM",
      itemId: null,
      description: "",
      quantityBreaks: [{ quantity: "1" }],
    };

    const nonDiscount = data.lines.filter((l) => l.type !== "DISCOUNT");
    const discount = data.lines.filter((l) => l.type === "DISCOUNT");
    onDataChange({ ...data, lines: [...nonDiscount, newLine, ...discount] });
  }, [data, readOnly, onDataChange]);

  const handleAddSectionLine = useCallback(() => {
    if (readOnly) {
      return;
    }
    const newLine: QuoteSectionLine = {
      id: generateQuoteLineId(),
      type: "SECTION",
      description: "New Section",
    };
    const nonDiscount = data.lines.filter((l) => l.type !== "DISCOUNT");
    const discount = data.lines.filter((l) => l.type === "DISCOUNT");
    onDataChange({ ...data, lines: [...nonDiscount, newLine, ...discount] });
  }, [data, readOnly, onDataChange]);

  const handleAddNoteLine = useCallback(() => {
    if (readOnly) {
      return;
    }
    const newLine: QuoteNoteLine = {
      id: generateQuoteLineId(),
      type: "NOTE",
      description: "",
    };
    const nonDiscount = data.lines.filter((l) => l.type !== "DISCOUNT");
    const discount = data.lines.filter((l) => l.type === "DISCOUNT");
    onDataChange({ ...data, lines: [...nonDiscount, newLine, ...discount] });
  }, [data, readOnly, onDataChange]);

  const handleAddDiscountLine = useCallback(() => {
    if (readOnly) {
      return;
    }
    const newLine: QuoteDiscountLine = {
      id: generateQuoteLineId(),
      type: "DISCOUNT",
      description: "Discount",
      discount: {
        type: "PERCENTAGE",
        percentage: "10",
      },
    };
    onDataChange({ ...data, lines: [...data.lines, newLine] });
  }, [data, readOnly, onDataChange]);

  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (readOnly) {
        return;
      }
      const nonDiscount = data.lines.filter((l) => l.type !== "DISCOUNT");
      const discount = data.lines.filter((l) => l.type === "DISCOUNT");
      const reordered = arrayMove(nonDiscount, oldIndex, newIndex);
      onDataChange({ ...data, lines: [...reordered, ...discount] });
    },
    [data, readOnly, onDataChange],
  );

  const handleUpdateLine = useCallback(
    (index: number, updatedLine: QuoteLine) => {
      if (readOnly) {
        return;
      }
      const newLines = [...data.lines];
      if (updatedLine.type !== "DISCOUNT") {
        const itemAndSectionLines = data.lines.filter(
          (l) => l.type !== "DISCOUNT",
        );
        const actualLine = itemAndSectionLines[index];
        const originalIndex = data.lines.indexOf(actualLine);
        if (originalIndex !== -1) {
          newLines[originalIndex] = updatedLine;
        }
      } else {
        const discountIdx = data.lines.findIndex(
          (l) =>
            l.type === "DISCOUNT" &&
            data.lines.filter((ll) => ll.type === "DISCOUNT").indexOf(l) ===
              index,
        );
        if (discountIdx !== -1) {
          newLines[discountIdx] = updatedLine;
        }
      }
      onDataChange({ ...data, lines: newLines });
    },
    [data, readOnly, onDataChange],
  );

  const handleRemoveLine = useCallback(
    (index: number) => {
      if (readOnly) {
        return;
      }
      const itemAndSectionLines = data.lines.filter(
        (l) => l.type !== "DISCOUNT",
      );
      const actualLine = itemAndSectionLines[index];
      const newLines = data.lines.filter((line) => line !== actualLine);
      onDataChange({ ...data, lines: newLines });
    },
    [data, readOnly, onDataChange],
  );

  const handleRemoveDiscountLine = useCallback(
    (index: number) => {
      if (readOnly) {
        return;
      }
      const discountIdx = data.lines.findIndex(
        (l) =>
          l.type === "DISCOUNT" &&
          data.lines.filter((ll) => ll.type === "DISCOUNT").indexOf(l) ===
            index,
      );
      if (discountIdx !== -1) {
        const newLines = data.lines.filter((_, idx) => idx !== discountIdx);
        onDataChange({ ...data, lines: newLines });
      }
    },
    [data, readOnly, onDataChange],
  );

  const handleUpdateDiscountLine = useCallback(
    (index: number, updatedLine: QuoteDiscountLine) => {
      if (readOnly) {
        return;
      }
      const newLines = [...data.lines];
      let discountCount = 0;
      for (let i = 0; i < newLines.length; i++) {
        if (newLines[i].type === "DISCOUNT") {
          if (discountCount === index) {
            newLines[i] = updatedLine;
            break;
          }
          discountCount++;
        }
      }
      onDataChange({ ...data, lines: newLines });
    },
    [data, readOnly, onDataChange],
  );

  const handleBOMUpdate = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleItemSave = useCallback(
    async (updateData: UpdateItemParams): Promise<true | string> => {
      if (!bomItemId) {
        return "No item selected for editing";
      }
      const { updateItem } = useItemsStore.getState();
      const { item, error } = await updateItem(bomItemId, updateData);
      if (item) {
        return true;
      }
      return error ?? "Failed to update item";
    },
    [bomItemId],
  );

  const handleOpenBOM = useCallback(
    (_index: number, item: Item, quantities: string[]) => {
      setBomItemId(item.id);
      setBomQuantities(quantities);
      setBomPanelOpen(true);
    },
    [],
  );

  // The open item's frozen snapshot item. Only finalized quotes carry a
  // snapshot, and unlike the quote's updatedAt it is never advanced by quote
  // lifecycle updates (archive, opportunity link, public access).
  const quotedItemSnapshot = useMemo(() => {
    if (!bomItemId || quote?.status !== QuoteStatusEnum.FINALIZED) {
      return undefined;
    }
    const snapshotLine = hydratedLines?.find(
      (line) => line.type === "ITEM" && line.itemId === bomItemId,
    );
    if (!snapshotLine || snapshotLine.type !== "ITEM") {
      return undefined;
    }
    return snapshotLine.item ?? undefined;
  }, [bomItemId, quote?.status, hydratedLines]);

  const handleSave = useCallback(() => {
    if (!onSave) {
      return;
    }
    // Validate lines before saving
    for (const line of data.lines) {
      if (line.type === "ITEM") {
        for (const qb of line.quantityBreaks) {
          if (qb.unitPriceOverride !== undefined) {
            const price = qb.unitPriceOverride;
            if (price === "" || isNaN(Number(price))) {
              toast.error(
                `Invalid unit price override "${price}" for quantity ${qb.quantity}. Please enter a valid number.`,
              );
              return;
            }
          }
        }
      }
    }
    onSave();
  }, [onSave, data.lines]);

  const handleCopyPublicLink = useCallback(() => {
    if (!quote?.id) {
      return;
    }
    const publicUrl = `${window.location.origin}/public/quotes/${quote.id}`;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public quote link copied to clipboard");
  }, [quote?.id]);

  const handleViewPublicQuote = useCallback(() => {
    if (!quote?.id) {
      return;
    }
    window.open(`/public/quotes/${quote.id}`, "_blank");
  }, [quote?.id]);

  const handleRevokePublicAccess = useCallback(
    () =>
      saveLifecycleUpdate(
        { publicAccessRevoked: true },
        "Public link revoked",
        "Failed to revoke public link",
      ),
    [saveLifecycleUpdate],
  );

  const handleRestorePublicAccess = useCallback(
    () =>
      saveLifecycleUpdate(
        { publicAccessRevoked: false },
        "Public link restored",
        "Failed to restore public link",
      ),
    [saveLifecycleUpdate],
  );

  return (
    <div className="h-full flex flex-col relative">
      {documentTabAvailable && (
        <div className="flex items-center border-b bg-background px-3 sm:px-6 py-2 shrink-0">
          <Tabs
            value={editorView}
            className="flex"
            onValueChange={(view) =>
              setEditorView(view === "document" ? "document" : "quote")
            }
          >
            <TabsList>
              <TabsTrigger value="quote" className="gap-1.5 px-4">
                <Pencil className="size-4" /> Quote
              </TabsTrigger>
              <TabsTrigger value="document" className="gap-1.5 px-4">
                <FileText className="size-4" /> Document
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}
      {canShowDocumentTab && quote?.id && publicQuote ? (
        <div className="flex-1 min-h-0">
          <QuoteDocumentTab
            quoteId={quote.id}
            publicQuote={publicQuote}
            assetUrls={quote.assetUrls ?? {}}
            readOnly={readOnly}
            allowEditing={allowPerQuoteDocumentEditing}
            onDocumentSaved={onDocumentSaved}
          />
        </div>
      ) : (
        <>
          {/* Scrollable Content (Header + Main) */}
          <div className="flex-1 overflow-y-auto pb-24">
            {/* Meta Bar */}
            {showHeader && (
              <QuoteMetaBar
                quote={quote}
                name={data.name}
                customerCompanyId={data.customerCompanyId}
                priceListId={data.priceListId}
                shippingAddressId={data.shippingAddressId}
                locationId={data.locationId}
                opportunityId={data.opportunityId}
                salesRepUserId={data.salesRepUserId}
                personIds={data.personIds}
                expirationDate={data.expirationDate}
                quoteDate={data.quoteDate}
                readOnly={readOnly}
                canEditOpportunity={canEditOpportunity}
                showLocationField={showLocationField}
                quoteSettings={quoteSettings}
                onNameChange={handleNameChange}
                onCustomerChange={handleCustomerChange}
                onPriceListChange={handlePriceListChange}
                onShippingAddressChange={handleShippingAddressChange}
                onLocationChange={handleLocationChange}
                onOpportunityChange={handleOpportunityChange}
                onSalesRepChange={handleSalesRepChange}
                onPeopleChange={handlePeopleChange}
                onExpirationChange={handleExpirationChange}
                onQuoteDateChange={handleQuoteDateChange}
                onFinalize={onFinalize}
                finalizeReviewThreadId={finalizeReviewThreadId}
                onCreateRevision={onCreateRevision}
                onCopyPublicLink={handleCopyPublicLink}
                onViewPublicQuote={handleViewPublicQuote}
                onRevokePublicAccess={
                  canManagePublicAccess ? handleRevokePublicAccess : undefined
                }
                onRestorePublicAccess={
                  canManagePublicAccess ? handleRestorePublicAccess : undefined
                }
                onArchive={onArchive}
                onUnarchive={onUnarchive}
                isArchiving={isArchiving}
              />
            )}

            {/* Archived Banner */}
            {quote?.archivedAt && (
              <div className="mx-3 sm:mx-6 mt-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/50">
                <Archive className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    This quote is archived
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500">
                    Archived on{" "}
                    {new Date(quote.archivedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
                {onUnarchive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onUnarchive}
                    disabled={isArchiving}
                    className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900"
                  >
                    {isArchiving ? (
                      <Loader className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <ArchiveRestore className="h-4 w-4 mr-1.5" />
                    )}
                    Unarchive
                  </Button>
                )}
              </div>
            )}

            {/* Custom Fields */}
            {customFieldsSchema &&
              Object.keys(customFieldsSchema.properties).length > 0 && (
                <div className="px-3 sm:px-6">
                  <CustomFieldsSection
                    schema={customFieldsSchema}
                    values={data.customFields}
                    readOnly={readOnly}
                    onChange={handleCustomFieldChange}
                  />
                </div>
              )}

            {/* Cover Letter */}
            {enableCoverLetter && (
              <>
                <CoverLetterSection
                  value={data.coverLetterMarkdown}
                  onChange={handleCoverLetterChange}
                  readOnly={readOnly}
                />
                <Separator />
              </>
            )}

            {/* Main Content */}
            <div>
              {/* Line Items Section */}
              <div className="px-3 sm:px-6 py-4">
                <h3 className="text-base sm:text-lg font-semibold mb-4">
                  Line Items
                </h3>
                <ExpandCustomFieldsToggle />
                <div className="-mx-3 sm:mx-0 overflow-x-auto">
                  <div className="px-3 sm:px-0">
                    <QuoteItemsTable
                      lines={nonDiscountLines}
                      hydratedLines={hydratedLines}
                      readOnly={readOnly}
                      currencyCode={baseCurrency}
                      currencyContext={currencyContext}
                      currencyContextDirty={currencyContextDirty}
                      onReorder={handleReorder}
                      onUpdateLine={handleUpdateLine}
                      onRemoveLine={handleRemoveLine}
                      onOpenBOM={handleOpenBOM}
                    />
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddItemLine}
                      disabled={isSaving}
                      className="flex-1 sm:flex-none min-w-[100px]"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddSectionLine}
                      disabled={isSaving}
                      className="flex-1 sm:flex-none min-w-[100px]"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Section
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddNoteLine}
                      disabled={isSaving}
                      className="flex-1 sm:flex-none min-w-[100px]"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Note
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* Discounts Section */}
              <Tooltip open={discountsDisabled ? undefined : false}>
                <TooltipTrigger asChild>
                  <div className="px-3 sm:px-6 py-4">
                    <h3 className="text-base sm:text-lg font-semibold mb-4">
                      Discount Lines
                    </h3>
                    <div className="-mx-3 sm:mx-0">
                      <div className="px-3 sm:px-0">
                        <QuoteDiscountsTable
                          lines={discountLines}
                          readOnly={readOnly || discountsDisabled}
                          currencyCode={baseCurrency}
                          currencyContext={currencyContext}
                          onUpdateLine={handleUpdateDiscountLine}
                          onRemoveLine={handleRemoveDiscountLine}
                        />
                      </div>
                    </div>
                    {!readOnly && !discountsDisabled && allowDiscountLines && (
                      <div className="mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddDiscountLine}
                          disabled={isSaving}
                          className="w-full sm:w-auto"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Discount
                        </Button>
                      </div>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{discountsDisabledReason}</p>
                </TooltipContent>
              </Tooltip>

              <Separator />

              {/* Totals Section */}
              <div className="px-3 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-4">
                  <h3 className="text-base sm:text-lg font-semibold">Totals</h3>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor="show-totals"
                      className="text-sm text-muted-foreground"
                    >
                      Show on public quote
                    </Label>
                    <Switch
                      id="show-totals"
                      checked={data.showTotals}
                      onCheckedChange={handleShowTotalsChange}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                {quote && data.showTotals && (
                  <QuoteTotalsFooter
                    quote={quote}
                    isDirty={isDirty}
                    lines={data.lines}
                    hydratedLines={hydratedLines}
                    showTotalWeight={showTotalWeight}
                    currencyContext={currencyContext}
                    currencyControl={
                      showCurrencySelector && availableCurrencies.length > 0 ? (
                        <QuoteCurrencySelector
                          quoteCurrency={data.quoteCurrency}
                          exchangeRate={displayRate}
                          baseCurrency={baseCurrency}
                          availableCurrencies={availableCurrencies}
                          readOnly={readOnly}
                          rateLoading={isRateLoading}
                          onCurrencyChange={handleCurrencyChange}
                        />
                      ) : undefined
                    }
                  />
                )}
                {!data.showTotals && (
                  <p className="text-sm text-muted-foreground italic">
                    Totals will be hidden on the public quote
                  </p>
                )}
              </div>

              <Separator />

              {/* Terms & Conditions Section */}
              <div className="px-3 sm:px-6 py-4">
                <h3 className="text-base sm:text-lg font-semibold mb-4">
                  Terms & Conditions
                </h3>
                <div className="space-y-4">
                  {/* Quote Footer */}
                  <TermsField
                    label="Quote Footer"
                    value={data.quoteFooter}
                    readOnly={readOnly}
                    onOpen={() => setFooterPanelOpen(true)}
                  />

                  {/* Delivery Terms */}
                  <TermsField
                    label="Delivery Terms"
                    value={data.deliveryTerms}
                    readOnly={readOnly}
                    onOpen={() => setDeliveryPanelOpen(true)}
                  />

                  {/* Payment Terms */}
                  <TermsField
                    label="Payment Terms"
                    value={data.paymentTerms}
                    readOnly={readOnly}
                    onOpen={() => setPaymentPanelOpen(true)}
                  />

                  {/* Terms of Service */}
                  <TermsField
                    label="Terms of Service"
                    value={data.termsOfService}
                    readOnly={readOnly}
                    onOpen={() => setTosPanelOpen(true)}
                  />
                </div>
              </div>
            </div>

            {quote?.id && (
              <div className="px-4 sm:px-6 pb-4">
                <NotesSection entityType="QUOTE" entityId={quote.id} />
              </div>
            )}
          </div>

          {/* Save/Cancel Actions */}
          {showActionButtons && !readOnly && (
            <div className="absolute bottom-0 left-0 right-0 bg-background border-t p-3 sm:p-4 z-10">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                {onCancel && (
                  <Button
                    variant="outline"
                    onClick={onCancel}
                    disabled={isSaving}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
                  className="w-full sm:w-auto"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : isDirty ? "Save Changes" : "Saved"}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Item Editor Panel (BOM + Image Preview) */}
      {bomItemId && !inlineEditorEnabled && (
        <ItemEditor
          open={bomPanelOpen}
          onOpenChange={setBomPanelOpen}
          itemId={bomItemId}
          mode="edit"
          onSave={handleItemSave}
          quantities={bomQuantities}
          onBomUpdated={handleBOMUpdate}
          defaultTab="bom"
          quotedItem={quotedItemSnapshot}
          currencyContext={currencyContext}
        />
      )}
      {bomItemId && inlineEditorEnabled && bomPanelOpen && (
        <div className="absolute inset-0 z-20 bg-background">
          <ItemEditor
            inline
            open
            onOpenChange={setBomPanelOpen}
            itemId={bomItemId}
            mode="edit"
            onSave={handleItemSave}
            quantities={bomQuantities}
            onBomUpdated={handleBOMUpdate}
            defaultTab="bom"
            quotedItem={quotedItemSnapshot}
            currencyContext={currencyContext}
            onToggleChat={onToggleChat}
            isChatCollapsed={isChatCollapsed}
            isAgentWorking={isAgentWorking}
          />
        </div>
      )}

      {/* Terms of Service Panel */}
      <RichTextEditorSheet
        open={tosPanelOpen}
        onOpenChange={setTosPanelOpen}
        title="Terms of Service"
        value={data.termsOfService}
        onChange={handleTermsOfServiceChange}
        placeholder="Enter terms of service..."
        readOnly={readOnly}
      />

      {/* Quote Footer Panel */}
      <RichTextEditorSheet
        open={footerPanelOpen}
        onOpenChange={setFooterPanelOpen}
        title="Quote Footer"
        value={data.quoteFooter}
        onChange={handleQuoteFooterChange}
        placeholder="Enter quote footer text..."
        readOnly={readOnly}
      />

      {/* Delivery Terms Panel */}
      <RichTextEditorSheet
        open={deliveryPanelOpen}
        onOpenChange={setDeliveryPanelOpen}
        title="Delivery Terms"
        value={data.deliveryTerms}
        onChange={handleDeliveryTermsChange}
        placeholder="Enter delivery terms..."
        readOnly={readOnly}
        note={
          !readOnly && (
            <CharacterLimitNote
              length={data.deliveryTerms.length}
              maxLength={quoteSettings?.maxDeliveryTermsLength ?? null}
            />
          )
        }
      />

      {/* Payment Terms Panel */}
      <RichTextEditorSheet
        open={paymentPanelOpen}
        onOpenChange={setPaymentPanelOpen}
        title="Payment Terms"
        value={data.paymentTerms}
        onChange={handlePaymentTermsChange}
        placeholder="Enter payment terms..."
        readOnly={readOnly}
      />
    </div>
  );
}

// ── Local Helpers ──

/**
 * Re-denominates document-currency line values into the base currency at the
 * outgoing snapshot, so a currency switch preserves their economic value.
 * @param lines - the editor's quote lines
 * @param context - the outgoing currency context
 * @returns lines with overrides and AMOUNT discounts converted to base
 */
const redenominateLinesToBase = (
  lines: QuoteLine[],
  context: QuoteCurrencyFields,
): QuoteLine[] =>
  mapDocumentCurrencyValues(
    lines,
    (value) => convertQuoteAmountToBase(value, context),
    (value) => convertQuoteAmountToBase(value, context),
  );

/**
 * Re-denominates base-currency line values into the incoming quote currency at
 * the newly fetched snapshot: overrides convert at full unit-price precision,
 * AMOUNT discounts round to the quote currency's minor units.
 * @param lines - lines holding base-currency values
 * @param context - the incoming currency context
 * @returns lines with overrides and AMOUNT discounts in the quote currency
 */
const redenominateLinesToQuote = (
  lines: QuoteLine[],
  context: QuoteCurrencyFields,
): QuoteLine[] =>
  mapDocumentCurrencyValues(
    lines,
    (value) => convertUnitPrice(value, context),
    (value) => convertQuoteLineAmount(value, context),
  );

/**
 * Maps every manual unit-price override and AMOUNT discount amount through the
 * given converters, leaving non-numeric (mid-edit) values and all other lines
 * untouched.
 * @param lines - the editor's quote lines
 * @param convertPrice - converter applied to unit-price overrides
 * @param convertAmount - converter applied to AMOUNT discount amounts
 * @returns the mapped lines
 */
const mapDocumentCurrencyValues = (
  lines: QuoteLine[],
  convertPrice: (value: string) => string,
  convertAmount: (value: string) => string,
): QuoteLine[] => {
  const isConvertible = (value: string) =>
    value !== "" && !Number.isNaN(Number(value));
  return lines.map((line) => {
    if (line.type === "ITEM") {
      return {
        ...line,
        quantityBreaks: line.quantityBreaks.map((qb) =>
          qb.unitPriceOverride !== undefined &&
          isConvertible(qb.unitPriceOverride)
            ? { ...qb, unitPriceOverride: convertPrice(qb.unitPriceOverride) }
            : qb,
        ),
      };
    }
    if (
      line.type === "DISCOUNT" &&
      line.discount.type === "AMOUNT" &&
      isConvertible(line.discount.amount)
    ) {
      return {
        ...line,
        discount: {
          type: "AMOUNT",
          amount: convertAmount(line.discount.amount),
        },
      };
    }
    return line;
  });
};

/**
 * The person ids after linking an opportunity: an empty draft takes the
 * opportunity's people, any other selection is kept.
 */
const prefillPersonIds = (
  personIds: string[],
  opportunityId: string | null,
): string[] => {
  if (personIds.length > 0 || !opportunityId) {
    return personIds;
  }
  const people =
    useOpportunitiesStore.getState().opportunities.get(opportunityId)?.people ??
    [];
  usePeopleStore.getState().upsertPeople(people);
  return people.map((person) => person.id);
};

// ── Local Components ──

interface TermsFieldProps {
  /** Label for the terms field */
  label: string;
  /** Current value (may be empty/undefined) */
  value: string;
  /** Whether the field is read-only */
  readOnly: boolean;
  /** Opens the editing sheet */
  onOpen: () => void;
}

/**
 * Inline display for a terms field with a preview and edit/add button.
 * @param props - TermsFieldProps
 * @returns Terms field element
 */
function TermsField({ label, value, readOnly, onOpen }: TermsFieldProps) {
  if (!value && readOnly) {
    return null;
  }

  if (!value) {
    return (
      <div className="flex items-center gap-3">
        <Label className="shrink-0">{label}</Label>
        <Button variant="outline" size="sm" onClick={onOpen}>
          <Plus className="h-4 w-4 mr-1" />
          Add {label}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="border rounded-md p-3 bg-muted/30">
        <div className="text-sm text-muted-foreground line-clamp-3 prose prose-sm dark:prose-invert max-w-none">
          <MarkdownReact>{value}</MarkdownReact>
        </div>
        <Button variant="ghost" size="sm" className="mt-2" onClick={onOpen}>
          {readOnly ? (
            <>
              <FileText className="h-4 w-4 mr-1" />
              View
            </>
          ) : (
            <>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
